"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Group, Text } from "@chakra-ui/react";
import { normalizePhoneNumber, parsePhoneNumberValue } from "@fluenceur/types";
import { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { CountrySelector } from "./CountrySelector";
import { PhoneInput } from "./PhoneInput";
import { DEFAULT_COUNTRY, getCountryData } from "./country-data";
export const PhoneNumberInput = forwardRef(({ name, value, defaultValue, onChange, onValueChange, placeholder, size = "md", variant = "outline", disabled = false, required = false, defaultCountry = DEFAULT_COUNTRY, country, locale = "en", labels, portalled = true, portalContainer, inputRef, id, colorPalette = "gray", error, autoFocus, "aria-label": ariaLabel, "aria-describedby": ariaDescribedBy, onFocus, onBlur, ...props }, ref) => {
    const generatedId = useId();
    const countries = useMemo(() => getCountryData(locale), [locale]);
    const [internalCountry, setInternalCountry] = useState(() => parsePhoneNumberValue(value ?? defaultValue)?.country ?? defaultCountry);
    const selectedCode = country === undefined ? internalCountry : country ?? undefined;
    const selectedCountry = countries.find((item) => item.value === selectedCode);
    const [phoneValue, setPhoneValue] = useState(() => {
        const initial = value ?? defaultValue ?? "";
        // Controlled callers keep their raw draft; profile forms using defaultValue
        // keep the familiar national display of an existing international number.
        return value === undefined && country !== null
            ? parsePhoneNumberValue(initial, selectedCode)?.formatNational() ?? initial
            : initial;
    });
    const lastEmitted = useRef(null);
    const emitValue = useCallback((display, code, countrySelected = false) => {
        const parsed = parsePhoneNumberValue(display, code);
        const international = normalizePhoneNumber(display, code) ?? "";
        const details = {
            formatted: display,
            international,
            national: international && parsed ? parsed.formatNational() : display,
            country: parsed?.country ?? code,
            isValid: Boolean(international),
            ...(countrySelected ? { countrySelected: true } : {}),
        };
        lastEmitted.current = details;
        onChange?.(international);
        onValueChange?.(details);
    }, [onChange, onValueChange]);
    const handleCountryChange = useCallback((next) => {
        if (country === undefined)
            setInternalCountry(next.value);
        // The selection is explicit. Never reuse the previous country's E.164
        // normalization for a national draft after changing the country.
        const display = phoneValue.trim().startsWith("+")
            ? parsePhoneNumberValue(phoneValue)?.formatNational() ?? phoneValue
            : phoneValue;
        setPhoneValue(display);
        emitValue(display, next.value, true);
    }, [country, emitValue, phoneValue]);
    const handlePhoneChange = useCallback((display) => {
        setPhoneValue(display);
        const detected = display.trim().startsWith("+")
            ? parsePhoneNumberValue(display)?.country
            : undefined;
        if (detected && country === undefined)
            setInternalCountry(detected);
        emitValue(display, detected ?? selectedCode);
    }, [country, emitValue, selectedCode]);
    const handlePhoneBlur = useCallback(() => {
        const parsed = parsePhoneNumberValue(phoneValue, selectedCode);
        if (parsed && normalizePhoneNumber(phoneValue, selectedCode)) {
            const formatted = parsed.country ? parsed.formatNational() : parsed.formatInternational();
            setPhoneValue(formatted);
            emitValue(formatted, parsed.country ?? selectedCode);
        }
        onBlur?.();
    }, [emitValue, onBlur, phoneValue, selectedCode]);
    useEffect(() => {
        if (value === undefined)
            return;
        const incoming = value ?? "";
        const emitted = lastEmitted.current;
        if (incoming === emitted?.formatted)
            return;
        // Existing onChange-only forms receive an empty canonical number while
        // typing. That acknowledgement must not erase the unfinished display.
        if (onChange && !onValueChange && incoming === emitted?.international)
            return;
        setPhoneValue(incoming);
        if (country === undefined) {
            const detected = parsePhoneNumberValue(incoming)?.country;
            if (detected)
                setInternalCountry(detected);
        }
    }, [country, onChange, onValueChange, value]);
    const international = normalizePhoneNumber(phoneValue, selectedCode) ?? "";
    const errorId = `${id ?? generatedId}-error`;
    const describedBy = [ariaDescribedBy, error ? errorId : undefined].filter(Boolean).join(" ") || undefined;
    return (_jsxs(Box, { ref: ref, width: "100%", ...props, children: [_jsxs(Group, { w: "full", attached: true, children: [_jsx(CountrySelector, { value: selectedCountry, onChange: handleCountryChange, disabled: disabled, size: size, locale: locale, labels: labels, portalled: portalled, portalContainer: portalContainer }), _jsx(PhoneInput, { id: id, inputRef: inputRef, value: phoneValue, onChange: handlePhoneChange, onBlur: handlePhoneBlur, onFocus: onFocus, placeholder: placeholder || selectedCountry?.placeholder, size: size, variant: variant, colorPalette: colorPalette, disabled: disabled, required: required, name: name, error: error, autoFocus: autoFocus, "aria-label": ariaLabel, "aria-describedby": describedBy })] }), name && _jsx("input", { type: "hidden", name: `${name}_full`, value: international }), error && _jsx(Text, { id: errorId, mt: "2", color: "fg.error", fontSize: "sm", children: error })] }));
});
PhoneNumberInput.displayName = "PhoneNumberInput";
